import type { Resource } from "@medplum/fhirtypes";
import { type MappingDetails, PersistenceMappingError } from "../engine/errors";

/**
 * The three ways a codec reads something it is not allowed to invent.
 *
 * A codec translates; it never substitutes a value the data does not contain.
 * The problem these solve is incentive, not vocabulary: `x as SomeEnum` is
 * eight characters and validating by hand is six lines, so the unchecked form
 * won every time. These make the correct form the short one.
 *
 * Both THROW rather than decide. Throwing is a refusal to decide, which leaves
 * the choice to `hydrate`, where policy legitimately lives. Reach for them only
 * where the DOMAIN requires a value: a field that is genuinely optional reads
 * absent as `undefined`, and that is translation, not a decision.
 */

function context(row: Resource, field: string, rest?: Partial<MappingDetails>): MappingDetails {
  return { resourceType: row.resourceType, ...(row.id ? { id: row.id } : {}), field, ...rest };
}

// Deliberately not `Type/id`: that spelling is a FHIR reference, and this is a
// row locator in a message about a row that could not be read.
function at(row: Resource): string {
  return `${row.resourceType} row '${row.id ?? "<unassigned>"}'`;
}

/** A field the domain requires. Absent is a malformed row, not a default. */
export function required<T>(row: Resource, field: string, value: T | undefined | null): T {
  if (value === undefined || value === null) {
    throw new PersistenceMappingError(
      `Stored ${at(row)} has no '${field}', which the domain requires`,
      "FIELD_MISSING",
      context(row, field),
    );
  }
  return value;
}

/**
 * A stored code the domain models as a closed set. The cast this replaces
 * asserted membership without checking, so a value outside the set became one
 * the domain believes impossible and nothing failed until something switched
 * on it.
 */
export function oneOf<T extends string>(
  row: Resource,
  field: string,
  value: string | undefined,
  allowed: readonly T[],
): T {
  const match = allowed.find((known) => known === value);
  if (!match) {
    throw new PersistenceMappingError(
      `Stored ${at(row)} has '${field}' of '${value ?? "nothing"}', which is not one of: ${allowed.join(", ")}`,
      value === undefined ? "FIELD_MISSING" : "VALUE_OUT_OF_RANGE",
      context(row, field, { found: value, allowed }),
    );
  }
  return match;
}

/**
 * A stored value that is not the SHAPE the domain requires — the third case,
 * and the only one whose caller has to do the looking first.
 *
 * `required` and `oneOf` both decide for themselves, because absence and set
 * membership are single comparisons. A shape is not: a field with no FHIR
 * structure behind it (our own JSON in a `valueString`, where `JSON.parse`
 * returns `any` and no type is left to check) is walked a level at a time, and
 * each level knows what it expected. So this takes the verdict and owns only
 * the refusal — the row locator, the problem code and the details — which is
 * what stops every such walk from inventing its own phrasing for "unreadable".
 *
 * `expected` completes "is not …": "an object", "a boolean", "an array".
 */
export function malformed(row: Resource, field: string, expected: string, found: unknown): never {
  throw new PersistenceMappingError(
    `Stored ${at(row)} has '${field}', which is not ${expected}`,
    found === undefined ? "FIELD_MISSING" : "VALUE_OUT_OF_RANGE",
    context(row, field, { found }),
  );
}

/** One-to-one code translation, with explicit decode-only legacy spellings. */
export function codeTranslation<Domain extends string, Fhir extends string>(
  toFhir: Record<Domain, Fhir>,
  decodeOnly: Partial<Record<Fhir, Domain>> = {},
) {
  const fromFhir = Object.fromEntries([
    ...Object.entries(toFhir).map(([domain, fhir]) => [fhir, domain]),
    ...Object.entries(decodeOnly),
  ]) as Record<string, Domain>;
  const allowed = Object.keys(fromFhir);
  return {
    toFhir,
    fromFhir: (row: Resource, field: string, value: string | undefined): Domain =>
      fromFhir[oneOf(row, field, value, allowed)] as Domain,
  };
}
